import { Helmet } from 'react-helmet-async';
import { useLang, type Lang } from '@/lib/i18n';

const BASE_URL = 'https://www.fernau-gmbh.de';

interface SEOHeadProps {
  /** Canonical path WITHOUT language prefix, always starting with `/`. e.g. `/consulting`. */
  path: string;
  /** Per-language title/description. If only `title`/`description` are passed they apply to both. */
  title: string | { de: string; en: string };
  description: string | { de: string; en: string };
  ogImage?: string;
  /** Either a boolean (applies to both languages) or per-language flags. */
  noIndex?: boolean | { de?: boolean; en?: boolean };
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Override the auto-derived hreflang set (rarely needed). */
  hreflang?: { lang: string; href: string }[];
}

function pick<T>(value: T | { de: T; en: T }, lang: Lang): T {
  if (value && typeof value === 'object' && 'de' in (value as object) && 'en' in (value as object)) {
    return (value as { de: T; en: T })[lang];
  }
  return value as T;
}

function localized(path: string, lang: Lang): string {
  if (lang === 'en') {
    return path === '/' ? '/en' : `/en${path}`;
  }
  return path;
}

const BRAND = 'FERNAU Präzisionstechnik GmbH';

export function SEOHead({
  title,
  description,
  path,
  ogImage = `${BASE_URL}/og-image.jpg`,
  noIndex = false,
  jsonLd,
  hreflang,
}: SEOHeadProps) {
  const lang = useLang();
  const resolvedTitle = pick(title, lang);
  const resolvedDescription = pick(description, lang);
  const resolvedNoIndex =
    typeof noIndex === 'boolean'
      ? noIndex
      : Boolean(noIndex?.[lang]);

  const canonicalPath = localized(path, lang);
  const canonical = `${BASE_URL}${canonicalPath}`;
  const fullTitle = path === '/' ? resolvedTitle : `${resolvedTitle} | ${BRAND}`;

  // Auto hreflang. Skip a language whose page is noindexed (Google ignores
  // alternates pointing to noindex URLs and treats the cluster as broken).
  const deNoIndex =
    typeof noIndex === 'boolean' ? noIndex : Boolean(noIndex?.de);
  const enNoIndex =
    typeof noIndex === 'boolean' ? noIndex : Boolean(noIndex?.en);
  const dePath = localized(path, 'de');
  const enPath = localized(path, 'en');
  const autoHreflang = [
    !deNoIndex && { lang: 'de', href: `${BASE_URL}${dePath}` },
    !deNoIndex && { lang: 'de-DE', href: `${BASE_URL}${dePath}` },
    !enNoIndex && { lang: 'en', href: `${BASE_URL}${enPath}` },
    !deNoIndex && { lang: 'x-default', href: `${BASE_URL}${dePath}` },
  ].filter(Boolean) as { lang: string; href: string }[];
  const hreflangSet = hreflang ?? autoHreflang;
  const ogLocale = lang === 'en' ? 'en_US' : 'de_DE';
  const ogLocaleAlt = lang === 'en' ? 'de_DE' : 'en_US';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="author" content={BRAND} />
      <meta name="publisher" content={BRAND} />
      <link rel="canonical" href={canonical} />
      {resolvedNoIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Hreflang */}
      {hreflangSet.map((h) => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />
      <meta property="og:site_name" content={BRAND} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
